import React from 'react';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import { ListItemContent } from '@mui/joy';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Button from '@mui/joy/Button';
import CircularProgress from '@mui/joy/CircularProgress';
import NumbersIcon from '@mui/icons-material/NumbersOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import ErrorOutlineSharpIcon from '@mui/icons-material/ErrorOutlineSharp';
import { SUBMISSION_PHASES } from './constants';


const PHASES = [
    { key: SUBMISSION_PHASES.PROCESSING, label: 'Processing', description: 'Preparing submission data' },
    { key: SUBMISSION_PHASES.VALIDATING, label: 'Validating', description: 'Checking eligibility and rules' },
    { key: SUBMISSION_PHASES.SUBMITTING, label: 'Submitting', description: 'Enrolling in course' },
    { key: SUBMISSION_PHASES.SUCCESS, label: 'Completed', description: 'Ready to launch training' },
];

const phaseIndex = (key) => PHASES.findIndex(p => p.key === key);

function PhaseDot({ config, phaseState, index }) {
    const { phase, isComplete } = phaseState;

    const dotSx = {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        animation: phase === config.key && !isComplete
            ? 'cvdScaleIn 450ms cubic-bezier(0.34, 2.26, 0.64, 1.8)'
            : 'none',
    };

    if (isComplete) {
        return (
            <Box sx={{ ...dotSx, bgcolor: 'success.solidBg' }}>
                <CheckRoundedIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
        );
    }

    if (phase === config.key) {
        if (config.key === SUBMISSION_PHASES.SUCCESS || config.key === SUBMISSION_PHASES.ERROR) {
            return null;
        }
        return (
            <Box sx={{
                ...dotSx,
                bgcolor: 'transparent',
                border: '2.5px solid #1976d2',
                boxShadow: '0 0 0 4px rgba(25,118,210,0.12)',
            }}>
                <CircularProgress
                    size="sm"
                    thickness={4}
                    sx={{ '--CircularProgress-size': '20px', color: '#1976d2' }}
                />
            </Box>
        );
    }

    return (
        <Box sx={{
            ...dotSx,
            bgcolor: 'neutral.softBg',
            border: '2px solid',
            borderColor: 'neutral.outlinedBorder',
            opacity: 0.5,
        }}>
            <Typography level="body-xs" sx={{ fontWeight: 500, color: 'text.tertiary', fontSize: 12 }}>
                {index + 1}
            </Typography>
        </Box>
    );
}

function PhaseLabel({ config, phaseState }) {
    const { phase, isComplete } = phaseState;
    const isActive = phase === config.key;
    const opacity = isActive || isComplete ? 1 : 0.4;
    const color = isComplete ? 'success.500' : isActive ? '#1976d2' : 'text.tertiary';

    return (
        <Box sx={{ opacity, transition: 'opacity 400ms ease', ml: 0.5 }}>
            <Typography
                level="title-sm"
                sx={{ fontWeight: isActive || isComplete ? 600 : 400, color, transition: 'color 400ms ease' }}
            >
                {config.label}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: 11, mt: 0.25 }}>
                {config.description}
            </Typography>
        </Box>
    );
}

function SubmissionProgress({ currentPhase, errorMessage, course_url, onDismiss, onSuccessDismiss }) {
    const isError = currentPhase === SUBMISSION_PHASES.ERROR;
    const isSuccess = currentPhase === SUBMISSION_PHASES.SUCCESS;
    const isRunning = !!currentPhase && !isError && !isSuccess;
    const isIdle = !currentPhase;

    const currentIdx = isRunning ? phaseIndex(currentPhase) : -1;

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 3.5, py: 3, px: 2,
            minWidth: 310, maxWidth: 460, width: '100%',
            animation: 'cvdFadeIn 350ms ease',
        }}>
            {/* ── IDLE: all phases pending ── */}
            {isIdle && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%', maxWidth: 370, opacity: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, justifyContent: 'center', px: 2 }}>
                        {PHASES.slice(0, 3).map((cfg, i) => (
                            <React.Fragment key={cfg.key}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'neutral.softBg', border: '2px solid', borderColor: 'neutral.outlinedBorder', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography level="body-xs" sx={{ fontWeight: 500, color: 'text.tertiary', fontSize: 12 }}>{i + 1}</Typography>
                                </Box>
                                {i < 2 && <Box sx={{ width: 48, height: 2.5, borderRadius: 1, bgcolor: 'neutral.outlinedBorder', mx: 0.25 }} />}
                            </React.Fragment>
                        ))}
                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Ready to submit this course version</Typography>
                    </Box>
                </Box>
            )}

            {/* ── SUCCESS RESULT ── */}
            {isSuccess && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, animation: 'cvdSlideUp 450ms ease' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        {PHASES.slice(0, 3).map((cfg, i) => (
                            <React.Fragment key={cfg.key}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'success.solidBg', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'cvdScaleIn 500ms cubic-bezier(0.34, 2.26, 0.64, 1.8)', boxShadow: '0 0 0 6px rgba(46,125,50,0.1)' }}>
                                    <CheckRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
                                </Box>
                                {i < 2 && <Box sx={{ width: 40, height: 2.5, bgcolor: 'success.500', borderRadius: 1 }} />}
                            </React.Fragment>
                        ))}
                    </Box>
                    <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'success.solidBg', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'cvdScaleIn 550ms cubic-bezier(0.34, 2.26, 0.64, 1.8)', boxShadow: '0 8px 30px rgba(46,125,50,0.25)' }}>
                        <CheckRoundedIcon sx={{ color: '#fff', fontSize: 42 }} />
                    </Box>
                    <Typography level="h4" sx={{ color: '#2e7d32', textAlign: 'center', mt: 0.5 }}>Submitted Successfully</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center' }}>Your course has been enrolled and is ready.</Typography>
                    {course_url && (
                        <Button size="md" variant="solid" color="success" startDecorator={<LaunchIcon />} onClick={() => window.open(course_url, '_blank')
                        } sx={{ mt: 1, borderRadius: '20px', px: 3 }}>
                            Launch Training
                        </Button>
                    )}
                    <Button size="sm" variant="plain" color="neutral" onClick={onSuccessDismiss || onDismiss} sx={{ mt: 0.5 }}>Dismiss</Button>
                </Box>
            )}

            {/* ── ERROR RESULT ── */}
            {isError && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, animation: 'cvdSlideUp 450ms ease' }}>
                    <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'danger.solidBg', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'cvdScaleIn 500ms cubic-bezier(0.94, 2.26, 0.64, 1)', boxShadow: '0 8px 30px rgba(198,40,40,0.2)' }}>
                        <CloseIcon sx={{ color: '#fff', fontSize: 42 }} />
                    </Box>
                    <Typography level="h4" sx={{ color: '#c62828', textAlign: 'center' }}>Submission Failed</Typography>
                    {errorMessage && (
                        <Box sx={{ background: 'rgba(244,67,54,0.05)', border: '1px solid rgba(244,67,54,0.2)', borderRadius: '10px', px: 2.5, py: 1.5, maxWidth: 380 }}>
                            <Typography level="body-sm" sx={{ color: '#c62828', textAlign: 'center' }}>{errorMessage}</Typography>
                        </Box>
                    )}
                    <Button size="sm" variant="soft" color="danger" onClick={onDismiss} sx={{ mt: 0.5, borderRadius: '20px', px: 3 }}>Dismiss & Retry</Button>
                </Box>
            )}

            {/* ── PROGRESS PHASES (running) ── */}
            {isRunning && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, width: '100%', maxWidth: 370 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, justifyContent: 'center', px: 2 }}>
                        {PHASES.slice(0, 3).map((cfg, i) => {
                            const isLast = i === 2;
                            const isComplete = i < currentIdx;
                            const isCurrent = i === currentIdx;
                            const phaseState = { phase: isCurrent ? currentPhase : null, isComplete };
                            return (
                                <React.Fragment key={cfg.key}>
                                    <PhaseDot config={cfg} phaseState={phaseState} index={i} />
                                    {!isLast && (
                                        <Box sx={{ width: 48, height: 2.5, borderRadius: 1, bgcolor: isComplete ? 'success.solidBg' : 'neutral.outlinedBorder', transition: 'background-color 500ms ease', mx: 0.25 }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', textAlign: 'center', animation: 'cvdStepEnter 350ms ease-out' }}>
                        {(() => {
                            const cfg = PHASES[currentIdx];
                            if (!cfg) return null;
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size="md" thickness={4} sx={{ '--CircularProgress-size': '48px', color: '#1976d2' }} />
                                    <Typography level="title-md" sx={{ color: '#1976d2', fontWeight: 600 }}>{cfg.label}</Typography>
                                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>{cfg.description}</Typography>
                                </Box>
                            );
                        })()}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1, px: 1 }}>
                        {PHASES.slice(0, 3).map((cfg, i) => {
                            const isComplete = i < currentIdx;
                            const isCurrent = i === currentIdx;
                            const phaseState = { phase: isCurrent ? currentPhase : null, isComplete };
                            return (
                                <Box key={cfg.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, px: 1.5, borderRadius: 'md', bgcolor: isCurrent ? 'rgba(25,118,210,0.06)' : 'transparent', transition: 'background-color 400ms ease', animation: isCurrent ? 'cvdStepEnter 300ms ease-out' : 'none' }}>
                                    <PhaseDot config={cfg} phaseState={phaseState} index={i} />
                                    <PhaseLabel config={cfg} phaseState={phaseState} />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
}


function WarningInline({ onProceed, onDiscard }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, animation: 'cvdScaleIn 400ms cubic-bezier(.36,.07,.19,.97)', maxWidth: 340, px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ErrorOutlineSharpIcon sx={{ fontSize: '4rem', color: '#016799' }} />
            </Box>
            <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                Course was enrolled within last 14 days.<br />
                Do you still want to continue?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                <Button color="success" variant="soft" onClick={onProceed}>Proceed</Button>
                <Button color="warning" variant="soft" onClick={onDiscard}>Discard</Button>
            </Box>
        </Box>
    );
}


export default function ExpandedCardView({
    item,
    submissionPhase,
    errorMessage,
    course_url,
    trainingProviders,
    showWarning,
    onWarningProceed,
    onWarningDiscard,
    onDismiss,
    onSuccessDismiss,
}) {
    if (!item) return null;

    if (showWarning) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <WarningInline onProceed={onWarningProceed} onDiscard={onWarningDiscard} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, overflow: 'auto' }}>
                {/* ── LEFT: Course info ── */}
                <Box sx={{ flex: '0 0 340px', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 1.5, p: 3, overflow: 'auto', borderColor: 'divider' }}>
                    <List size="md" sx={{ '--List-gap': '2px', '--ListItem-paddingY': '8px' }}>
                        <ListItem>
                            <ListItemDecorator><NumbersIcon color="action" /></ListItemDecorator>
                            <ListItemContent>
                                <Typography level="body-sm">Version</Typography>
                                <Typography level="title-sm" noWrap>{item.versionNo}</Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemDecorator><TimerOutlinedIcon color="action" /></ListItemDecorator>
                            <ListItemContent>
                                <Typography level="body-sm">Training Duration</Typography>
                                <Typography level="title-sm" noWrap>{item.trainDuration}</Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemDecorator><CorporateFareOutlinedIcon color="action" /></ListItemDecorator>
                            <ListItemContent>
                                <Typography level="body-sm">Training Provider</Typography>
                                <Typography level="title-sm" noWrap>{trainingProviders?.[parseInt(item.orgIssued)]}</Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemDecorator>
                                {item?.completion_date !== ""
                                    ? <CheckCircleOutlineIcon color="action" />
                                    : <RemoveCircleOutlineIcon color="action" />}
                            </ListItemDecorator>
                            <ListItemContent>
                                <Typography level="body-sm">Last registered</Typography>
                                <Typography level="title-sm" noWrap>{item.completion_date ? item.completion_date : 'N/A'}</Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>
                </Box>

                {/* ── RIGHT: Progress / Result ── */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, overflow: 'auto' }}>
                    <SubmissionProgress
                        currentPhase={submissionPhase}
                        errorMessage={errorMessage}
                        course_url={course_url}
                        onDismiss={onDismiss}
                        onSuccessDismiss={onSuccessDismiss}
                    />
                </Box>
            </Box>
        </Box>
    );
}